import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";


export interface CascadingOption {
  label: string;
  value: string;
  children?: CascadingOption[];
}

interface CascadingSelectProps {
  options: CascadingOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  /** Extra class for the popover panel — used to scope-override accent colors (see FreePracticeDialog). */
  popoverClassName?: string;
}

export function CascadingSelect({
  options,
  value,
  onChange,
  placeholder = "Select options",
  className,
  popoverClassName,
}: CascadingSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [expandedParents, setExpandedParents] = React.useState<Set<string>>(
    new Set()
  );

  // 获取选中项的显示标签
  const getSelectedLabels = () => {
    const labels: string[] = [];
    options.forEach((parent) => {
      if (parent.children) {
        parent.children.forEach((child) => {
          if (value.includes(child.value)) {
            labels.push(`${parent.label} > ${child.label}`);
          }
        });
      }
    });
    return labels;
  };

  const selectedLabels = getSelectedLabels();

  // 切换父级展开/收起
  const toggleParent = (parentValue: string) => {
    const newExpanded = new Set(expandedParents);
    if (newExpanded.has(parentValue)) {
      newExpanded.delete(parentValue);
    } else {
      newExpanded.add(parentValue);
    }
    setExpandedParents(newExpanded);
  };

  // 选择/取消选择子项
  const toggleChild = (childValue: string) => {
    const newValue = value.includes(childValue)
      ? value.filter((v) => v !== childValue)
      : [...value, childValue];
    onChange(newValue);
  };



  // 清空所有选择
  const clearAll = () => {
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between min-h-10 h-auto flex bg-muted hover:bg-muted",
            // 手机横屏(高≤500)按 Figma 844×390：触发钮高 30 / 圆角 9 / 字 12；PC/平板不触发
            "[@media(max-height:500px)]:min-h-[30px]! [@media(max-height:500px)]:h-[30px]! [@media(max-height:500px)]:rounded-[9px]! [@media(max-height:500px)]:px-2.5! [@media(max-height:500px)]:text-xs!",
            className
          )}
        >
          <div className="flex gap-1 flex-wrap flex-1 min-w-0">
            {selectedLabels.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              // 只显示选中项的数量
              <span className="text-sm [@media(max-height:500px)]:text-xs!">
                {t("component.itemsSelected", { count: selectedLabels.length })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {selectedLabels.length > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearAll();
                }}
                className="hover:bg-accent rounded-sm p-1"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-full p-0", popoverClassName)} align="start">
        <Command>
          <CommandInput placeholder={t("component.searchPlaceholder")} className="[@media(max-height:500px)]:text-xs!" />
          {/* 手机横屏(高≤500)：列表夹到 Radix 计算的可用高度−搜索框高，避免下拉溢出屏底；PC/平板不触发 */}
          <CommandList className="[@media(max-height:500px)]:max-h-[calc(var(--radix-popover-content-available-height)_-_2.75rem)]!">
            <CommandEmpty>{t("component.noResults")}</CommandEmpty>
            <CommandGroup>
              {options.map((parent) => (
                <div key={parent.value}>
                  {/* 父级项 */}
                  <CommandItem
                    onSelect={() => toggleParent(parent.value)}
                    className="cursor-pointer font-medium [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:py-1! [@media(max-height:500px)]:font-normal!"
                  >
                    <ChevronDown
                      className={cn(
                        "mr-2 h-4 w-4 transition-transform [@media(max-height:500px)]:size-3!",
                        expandedParents.has(parent.value) ? "" : "-rotate-90"
                      )}
                    />
                    {parent.label}
                  </CommandItem>

                  {/* 子级项 */}
                  {expandedParents.has(parent.value) &&
                    parent.children?.map((child) => (
                      <CommandItem
                        key={child.value}
                        onSelect={() => toggleChild(child.value)}
                        className="cursor-pointer pl-8 [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:py-1!"
                      >
                        {/* 手机横屏：勾选框按 Figma 改圆形+缩小；选中绿(#9cea4f)由 DIALOG_POPOVER_ACCENT 驱动 */}
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary [@media(max-height:500px)]:rounded-full! [@media(max-height:500px)]:size-3.5!",
                            value.includes(child.value)
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <Check className="h-4 w-4 [@media(max-height:500px)]:size-3!" />
                        </div>
                        {child.label}
                      </CommandItem>
                    ))}
                </div>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

